import { registerPlugin } from '@capacitor/core';
import { useSyncExternalStore } from 'react';

/**
 * Ролик урока в ОТДЕЛЬНОМ нативном WKWebView поверх карточки — мост к Swift-плагину
 * `ios/App/App/InlineVideoPlugin.swift`.
 *
 * ⚠️ ВЕТКА video-native-webview (16.09.2026, техдолг п. 232). Проба 57: рамка `/embed/` в основном WebView убивается
 * ядром по памяти (jetsam PERPROCESSLIMIT) — она делит процесс с уроком. У отдельного WKWebView свой процесс и свой
 * лимит. Проба 59 на iPhone: играет, 153 нет, знак виден, едет с прокруткой, урок жив.
 *
 * Проба 60: все видеоблоки урока, живой вид ОДИН (`id` у каждой команды), подгонка при сдвиге раскладки, отступы
 * закреплённых панелей для маски.
 *
 * Журнал попыток: docs/platform/06-frontend/05-video-in-app-attempts.md
 */

export type NativeVideoRect = { x: number; y: number; width: number; height: number };
/** Сколько сверху и снизу видимой области закрыто панелями урока — по ним плагин обрезает ролик. */
export type NativeVideoInsets = { insetTop: number; insetBottom: number };
export type NativeVideoPlacement = NativeVideoRect & NativeVideoInsets;

type InlineVideoPlugin = {
  open(options: NativeVideoPlacement & { id: string; url: string; radius: number }): Promise<void>;
  update(options: NativeVideoPlacement & { id: string }): Promise<void>;
  /** `reason` — почему страница закрывает ролик; плагин пишет его в журнал `OBVideo`. */
  close(options: { id: string; reason: string }): Promise<void>;
};

/**
 * Открывать ли ролик нативным видом. Плагин `InlineVideo` есть ТОЛЬКО на iOS.
 *
 * ⚠️ 30.09.2026: раньше платформа не проверялась — флаг `video_native` один на обе системы (по
 * умолчанию включён), и в Android-сборке вызов плагина падал бы в «Видео не загрузилось» вместо
 * окна с роликом. Android 101 в Play собран до слияния нативного видео и не задет; следующая
 * сборка задела бы. На Android — прежнее окно (`openVideoOutside`).
 */
export function nativeVideoWanted(o: { native?: boolean; store: boolean; platform: string; flag: boolean }): boolean {
  return !!o.native && o.store && o.platform === 'ios' && o.flag;
}

/** Имя обязано совпадать с `jsName` в Swift — сверяет проверка. */
const plugin = registerPlugin<InlineVideoPlugin>('InlineVideo');

/**
 * Обычный объект поверх плагина: у `registerPlugin` прокси, подменить его методы в проверке нельзя,
 * а поведение «открыл — подогнал — закрыл при уходе» проверять обязательно.
 */
export const nativeVideo = {
  open: (options: NativeVideoPlacement & { id: string; url: string; radius: number }) => plugin.open(options),
  update: (options: NativeVideoPlacement & { id: string }) => plugin.update(options),
  close: (options: { id: string; reason: string }) => plugin.close(options),
};

/**
 * Причина закрытия для журнала: что случилось и какого размера была страница в этот момент.
 *
 * ⚠️ ЗАЧЕМ РАЗМЕР (01.10.2026). Полный экран открывался и сам закрывался: по коду — потому что страница стала шире
 * 768 и урок перерисовался другой раскладкой. `console` основного WebView в Console на маке не попадает, поэтому
 * причину страница отдаёт плагину, а он пишет её рядом со своими строками.
 */
export function closeReason(what: string, win: { innerWidth: number; innerHeight: number }): string {
  return `${what} ${Math.round(win.innerWidth)}x${Math.round(win.innerHeight)}`;
}

/**
 * Прямоугольник карточки В КООРДИНАТАХ СТРАНИЦЫ. Нативный вид кладётся в `scrollView` основного
 * WebView, у которого начало — верх документа (`contentInset: 'never'`), а не верх экрана.
 */
export function pageRect(
  box: { left: number; top: number; width: number; height: number },
  scroll: { scrollX: number; scrollY: number },
): NativeVideoRect {
  return {
    x: Math.round(box.left + scroll.scrollX),
    y: Math.round(box.top + scroll.scrollY),
    width: Math.round(box.width),
    height: Math.round(box.height),
  };
}

/** Метка закреплённой панели, под которой ролик обрезается: нижняя панель, полоска записи, «нет сети». */
export const COVER_ATTR = 'data-native-video-cover';

/**
 * Отступы от закреплённых панелей: панель в верхней половине экрана закрывает сверху до своего низа, в нижней —
 * снизу от своего верха. Невидимые (нулевой высоты, за экраном) не считаются. Статус-бар плагин добавит сам.
 */
export function coverInsets(
  covers: { top: number; bottom: number; height: number }[],
  viewportHeight: number,
): NativeVideoInsets {
  let insetTop = 0;
  let insetBottom = 0;
  for (const r of covers) {
    if (r.height <= 0 || r.bottom <= 0 || r.top >= viewportHeight) continue;
    if (r.top < viewportHeight / 2) insetTop = Math.max(insetTop, Math.round(r.bottom));
    else insetBottom = Math.max(insetBottom, Math.round(viewportHeight - r.top));
  }
  return { insetTop, insetBottom };
}

export function readCoverInsets(doc: Document, win: { innerHeight: number }): NativeVideoInsets {
  const rects = Array.from(doc.querySelectorAll(`[${COVER_ATTR}]`)).map((el) => el.getBoundingClientRect());
  return coverInsets(rects, win.innerHeight);
}

export function samePlacement(a: NativeVideoPlacement | null, b: NativeVideoPlacement): boolean {
  return (
    !!a &&
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height &&
    a.insetTop === b.insetTop &&
    a.insetBottom === b.insetBottom
  );
}

/**
 * Какая карточка сейчас держит нативный вид. Живой — один: нажал другой ролик — прежняя карточка перестаёт быть
 * живой, и плагин по новому `open` убирает прежний вид сам.
 */
let active: string | null = null;
/**
 * Какой ЭКЗЕМПЛЯР карточки держит вид. `id` ролика устойчивый (страница + ролик) и переживает перерисовку, а
 * экземпляр — нет; два блока с одним роликом на странице получают один `id`, и без хозяина оба двигали бы вид.
 */
let holder: string | null = null;
/** Отложенное закрытие: карточка ушла, но может вернуться в этом же проходе React (перерисовка). */
let pending: { id: string } | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

/** Устойчивое имя вида: одно и то же до и после перерисовки карточки. */
export function nativeVideoId(pathname: string, videoId: string): string {
  return `${pathname}#${videoId}`;
}

export function claimNativeVideo(id: string, who: string = id): void {
  if (active === id && holder === who) return;
  active = id;
  holder = who;
  emit();
}

/** Освободить — только своё: вытесненная карточка, уходя, не гасит живую. Возвращает, была ли своя. */
export function releaseNativeVideo(id: string, who: string = id): boolean {
  if (active !== id || holder !== who) return false;
  active = null;
  holder = null;
  emit();
  return true;
}

/**
 * Карточка размонтирована — закрыть её вид, НО НЕ СРАЗУ.
 *
 * ⚠️ ЗАЧЕМ ОТСРОЧКА (01.10.2026, 1.1.0 на iPhone). Размонтирование — это не только «ученик ушёл со страницы».
 * Урок сменил раскладку (страница стала шире 768 под полноэкранным роликом) — React снёс карточку и тут же создал
 * такую же; прежняя, уходя, закрывала вид, и полный экран схлопывался через секунду после открытия. Закрывать ролик
 * можно по действию ученика или уходу со страницы, не по перерисовке.
 *
 * React снимает эффекты ушедших карточек и ставит эффекты новых в одном синхронном проходе. Закрытие уходит в
 * микрозадачу — после этого прохода: вернулась такая же карточка (`adoptNativeVideo`) — закрытие снято; не
 * вернулась — вид закрыт. Микрозадача, а не таймер: под полноэкранным роликом основной WebView скрыт, и таймеры
 * скрытой страницы тормозятся.
 */
export function releaseNativeVideoLater(id: string, who: string, close: () => void): boolean {
  if (active !== id || holder !== who) return false;
  const mine = { id };
  pending = mine;
  queueMicrotask(() => {
    // Вид забрала вернувшаяся карточка, или его вытеснил другой ролик.
    if (pending !== mine) return;
    pending = null;
    if (releaseNativeVideo(id, who)) close();
  });
  return true;
}

/** Такая же карточка смонтирована заново, пока прежняя ждёт закрытия, — вид переходит ей, закрытие снимается. */
export function adoptNativeVideo(id: string, who: string): boolean {
  if (!pending || pending.id !== id || active !== id) return false;
  pending = null;
  holder = who;
  emit();
  return true;
}

/** Только для проверок: чистое состояние между сценариями. */
export function __resetNativeVideoForTests(): void {
  active = null;
  holder = null;
  pending = null;
}

export function activeNativeVideo(): string | null {
  return active;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function owner(): string {
  return active === null ? '' : `${active} | ${holder}`;
}

export function useNativeVideoActive(id: string, who: string = id): boolean {
  return useSyncExternalStore(subscribe, owner, owner) === `${id} | ${who}`;
}
